import React from 'react';

import { Stack, IconButton, Typography, ChevronDownIcon } from '@metarouter-private/mr-mui';

import WithTooltip from 'app/components/WithTooltip';
import { useDrawerContext } from 'app/shared/contexts/drawer';
import { useEventDebuggerTableContext } from 'app/event-monitoring/event-debugger/event-debugger-table/EventDebuggerTableContext';

const ROTATE_180 = { transform: 'rotate(180deg)' };

export default function DebuggerDrawerNavigation() {
  const { drawer, launchDrawer } = useDrawerContext();
  const { events } = useEventDebuggerTableContext();
  const { eventData } = drawer.data;

  // Reference-equality lookup: `events` is stable for the drawer's lifetime because
  // any refresh/filter/cluster change is blocked by the modal Drawer backdrop. If the
  // drawer ever becomes non-modal, switch to id-based lookup (e.g. eventId).
  const currentRowIndex = events.findIndex(event => event === eventData);
  const prevRow = currentRowIndex > 0 ? events[currentRowIndex - 1] : null;
  const nextRow = currentRowIndex >= 0 && currentRowIndex < events.length - 1 ? events[currentRowIndex + 1] : null;

  const onPrevRow = () => navigateToRow({ targetRow: prevRow, launchDrawer });
  const onNextRow = () => navigateToRow({ targetRow: nextRow, launchDrawer });

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <WithTooltip show title="Previous Event">
        <IconButton size="small" data-test-id="previousEventNavigation" disabled={prevRow === null} onClick={onPrevRow}>
          <ChevronDownIcon style={ROTATE_180} />
        </IconButton>
      </WithTooltip>
      <WithTooltip show title="Next Event">
        <IconButton size="small" data-test-id="nextEventNavigation" disabled={nextRow === null} onClick={onNextRow}>
          <ChevronDownIcon />
        </IconButton>
      </WithTooltip>
      {currentRowIndex >= 0 && (
        <Typography variant="caption" color="text.secondary" pl={0.5} data-test-id="eventPositionCounter">
          {currentRowIndex + 1} of {events.length}
        </Typography>
      )}
    </Stack>
  );
}

// Row navigation preserves the active tab as-is. If the target row's active tab is
// empty, the content components render an empty-state placeholder ("No output event
// for this row.") instead of swapping the tab out from under the user.
function navigateToRow({ targetRow, launchDrawer }) {
  if (targetRow === null) return;
  launchDrawer({ eventData: targetRow });
}
